/**
 * Simplified Chinese translation bundle. Mirrors `vi.ts` structure
 * exactly — every key must exist with the same shape.
 */

const zh = {
  common: {
    // GA-M-05/GA-W-07 hardcode sweep (2026-06-05) — moved out of TSX
    todayShort: "今天",
    processing: "处理中",
    daysShort: "{{d}} 天前",
    weeksShort: "{{n}} 周前",
    monthsShort: "{{n}} 个月前",
    backToDashboard: "← 返回仪表盘",
    a11yClose: "关闭",
    a11yCloseDialog: "关闭对话框",
    a11yOpenGuide: "打开 30 秒指南",
    a11yNewUpdates: "有新内容",
    a11yNewItems: "{{count}} 个新项目",
    appName: "iSale",
    save: "保存",
    cancel: "取消",
    close: "关闭",
    clear: "清除",
    delete: "删除",
    confirm: "确认",
    continue: "继续",
    back: "返回",
    backTo: "返回{{parent}}",
    next: "下一步 →",
    loading: "加载中…",
    search: "搜索…",
    saving: "保存中…",
    creating: "创建中…",
    error: "出错了",
    retry: "重试",
    skip: "跳过",
    done: "完成",
    yes: "是",
    no: "否",
    seeAll: "查看全部 →",
    saveFailed: "保存失败 · {{error}}",
    deleteFailed: "删除失败 · {{error}}",
    createFailed: "创建{{what}}失败 · {{error}}",
    loadFailed: "加载失败 · {{error}}",
    createClipFailed: "创建短片失败 · {{error}}",
    createCheckoutFailed: "无法启动结账 · {{error}}",
    deleteAccountFailed: "删除账户失败 · {{error}}",
    // EP-8 offline banner
    offlineBanner: "无网络连接 — 显示已缓存的数据",
    offlineBannerNoCache: "无网络连接 — 重新连接以加载数据",
  },
  splash: {
    /** Tagline below the iSale wordmark on the boot splash (Hi-Fi 01). */
    tagline: "AI 销售短视频 · 3 分钟搞定",
  },
  signIn: {
    heroLine: "打开 App,\n3 分钟出片。",
    heroLine1: "打开 App,",
    heroLine2: "3 分钟出片。",
    subtitle: "登录即可制作首个产品短片 — 可发布到 TikTok、Reels、Shorts 与 Shopee Video，无需剪辑技能。",
    subCopy:
      "登录即可制作首个产品短片 — 可发布到 TikTok、Reels、Shorts 与 Shopee Video，无需剪辑技能。与 iPhone 和 Android 上的 iSale 应用同步。",
    tagline: "AI 销售短片 · web",
    googleButton: "使用 Google 继续",
    appleButton: "使用 Apple 继续",
    continueGoogle: "使用 Google 继续",
    continueApple: "使用 Apple 继续",
    continueEmail: "使用邮箱登录",
    or: "或",
    redirecting: "正在跳转…",
    appleNotConfigured: "Apple 登录即将上线，请暂时使用 Google。",
    emailComingSoon: "邮箱登录将在 Phase 2 推出。请暂时使用 Google 或 Apple。",
    /** FL-6: shown when APPLE_CLIENT_ID is not configured (Apple button hidden). */
    emailComingSoonGoogleOnly: "邮箱登录将在 Phase 2 推出。请暂时使用 Google。",
    trustSpeed: "约 3 分钟 / 短片",
    trustNative: "为越南打造",
    legalPrefix: "登录即表示同意",
    legalTerms: "服务条款",
    legalPrivacy: "隐私政策",
    legalAnd: "和",
    terms: "服务条款",
    privacy: "隐私政策",
    captionHook: "皮肤干如沙漠?",
  },
  home: {
    greetingMorning: "早上好",
    greetingAfternoon: "下午好",
    greetingEvening: "晚上好",
    greetingNight: "夜深了还在忙",
    quotaLabel: "本月",
    quotaUsageSuffix: "个视频已用",
    upgradeCta: "升级 →",
    recentProducts: "最近商品",
    recentClips: "最近视频",
    fabLabel: "新建视频",
    statusReady: "待渲染",
    statusDraft: "草稿",
    statusArchived: "已归档",
    statusRendered: "已完成",
    greetingAvatarA11y: "打开账户",
    greetingBellA11y: "打开通知",
    emptyClipsHint: "还没有视频 — 点击\"{{cta}}\"开始",
  },
  settings: {
    // GA-X-01 Option B (2026-06-05): toggle → real status row
    aiConsentAccepted: "已同意 · {{date}}",
    aiConsentNeedsReconsent: "请重新同意最新条款",
    aiConsentNotAccepted: "尚未同意",
    title: "设置",
    sectionPermissions: "权限和数据",
    sectionNotifications: "通知",
    sectionOther: "其他",
    sectionAccount: "账户",
    signOutNote: "退出当前设备登录会话",
    aiConsent: "AI 同意",
    aiConsentNote: "3 个 provider · 自 2026年5月",
    auditLog: "AI 活动日志",
    auditLogNote: "查看每次 AI 调用",
    requestDelete: "请求删除数据",
    requestDeleteNote: "GDPR · ATTP",
    notifyRender: "视频渲染完成时",
    notifyTips: "短视频带货小贴士",
    notifyPromo: "套餐促销",
    language: "语言",
    appearance: "外观",
    themeLight: "浅色",
    themeDark: "深色",
    themeSystem: "跟随系统",
    signOut: "退出登录",
    accountGuest: "访客",
    accountUserFallback: "你好 👋",
    accountWorkspaceFallback: "个人工作区",
    accountPlanFree: "免费版",
    requestDeleteNoteLong: "GDPR · ATTP · 30 天软删除",
    complianceDict: "内容审核规则",
    complianceDictNote: "查看 AI 标记的所有关键词 · 每周更新",
    aiConsentNoteShort: "3 个 provider · 自 2026年5月",
    auditLogNoteShort: "查看每次 AI 调用",
    billingMenuTitle: "套餐与配额",
    billingMenuNote: "查看配额 · 升级 Pro",
    notificationsMenuTitle: "通知",
    notificationsMenuNote: "渲染、配额、促销",
    footerBuild: "iSale v0.1.0 · 构建 {{date}}",
  },
  billing: {
    title: "套餐",
    currentPlan: "当前套餐",
    videosThisMonth: "本月视频",
    resetOn: "重置于",
    starterName: "入门版",
    starterTagline: "新店铺 · 30 个短片足够试水",
    proName: "专业版",
    proTagline: "扩张型店铺 · 每天 5 条,稳住更新节奏",
    enterprise: "企业版",
    contactSales: "联系",
    chooseStarter: "选择入门版",
    choosePro: "选择专业版",
    using: "使用中",
    freeName: "免费版",
    freeTagline: "够您试用并发布几个短视频",
    lifetimeName: "终身版",
    lifetimeTagline: "一次付费,永久使用 — 限前 200 名购买者",
    registerEarly: "早期注册",
    popular: "最受欢迎",
    footer: "随时取消 · 7 天退款 · 支持 Momo / ZaloPay / Visa",
    // MP11 §3.3 — Pro Annual upsell
    proAnnualTitle: "专业版 · 年付",
    proAnnualPrice: "{{price}}/年",
    periodSavingsBadge: "省 2 个月",
    proAnnualSavings: "比月付节省 {{saved}}/年",
    proAnnualSaveBadge: "省 20%",
    proAnnualSubtitle: "相当于 {{monthly}}/月",
    periodSelectMonth: "月付",
    periodSelectYear: "年付",
  },
  facts: {
    title: "Fact Locker",
    headerHook: "先核实,再生成",
    heroTitle: "确认 AI 发现的事实",
    introBody:
      "下方每个 fact 在发送给脚本 LLM 之前都需要您核实。这是反幻觉护栏 — 每个 claim 由您最终负责。",
    verifiedLabel: "已核实",
    verifiedNeeded: "需要",
    riskyWarningTitle: "个 fact 被合规标记",
    riskyWarningBody:
      "带 ⚠️ 的 fact 含有违反越南规则(化妆品/保健品/药品)的字眼。在核实之前请修改或附上证据。",
    fieldSellingPoint: "核心卖点",
    fieldTargetAudience: "目标受众",
    fieldIngredient: "成分",
    fieldClaim: "宣称",
    fieldCategoryInference: "AI 类别推断",
    ctaCreateScript: "创建短片 →",
    ctaCreateScriptNeeded: "再核实 {{n}} 个 fact",
    createScriptFailed: "无法创建脚本 · 请重试",
    productNotReady: "产品尚未就绪 — 请等待 AI 分析完成后再试",
    addFieldCta: "添加",
    emptyFieldHint: "— 暂无信息 —",
  },
  clipFlow: {
    entryCta: "制作短视频",
    resumeTitle: "继续制作",
    resumeCta: "继续 →",
    resumeChip: "↻ 进行中",
    step1: "产品",
    step2: "脚本",
    step3: "编辑",
    step4: "导出",
    stepLabel: "第 {{n}}/4 步",
    navNext: "下一步 →",
    navBack: "← 返回",
    navSaveExit: "保存草稿并退出",
    exitConfirmTitle: "退出创建视频？",
    exitConfirmBody: "返回首页？你可以稍后继续这个草稿。",
    exitConfirmLeave: "退出",
    factGateTitle: "确认以便 AI 准确撰写",
    factGateBody: "AI 只根据你确认的信息撰写 —— 不会编造。",
    factGateProgress: "已确认 {{done}}/{{total}}",
    factGateDone: "已确认足够 ✓",
    factGateRegulated: "⚠ 需证件",
    captionLabel: "发布文案",
    captionRegenerate: "↻ 重新生成文案",
    exportReady: "短视频已就绪 🎉",
    exportPlatforms: "发布到平台",
    finishCta: "完成 · 再做一个 →",
    entryNoProduct: "请选择一个产品开始制作短视频。",
    entryProductError: "无法打开该产品 —— 可能已被删除或你没有权限。",
    entryMissing: "该步骤缺少所需信息，请从产品重新开始。",
    entryPickProduct: "选择产品 →",
    advancedTitle: "高级选项",
    advancedHint: "选择脚本类型 —— 默认「体验评测」",
    advancedOpen: "展开 ▾",
    advancedClose: "收起 ▴",
  },
  clipFormats: {
    review: "体验评测",
    unboxing: "开箱",
    beforeAfter: "使用前后",
    demo: "使用教程",
    story: "故事",
    promotion: "促销",
    testimonial: "用户好评",
    faq: "问答",
  },
  clipTemplates: {
    // GA-M-05/GA-W-07 hardcode sweep (2026-06-05)
    unknownType: "该视频类型不存在",
    detailNextStepHint: "下一步：输入产品名称并上传图片",
    sectionTitle: "从模板创建短视频",
    sectionSubtitle: "iSale 按正确结构和受众语气编写脚本",
    sectionBadge: "★ 新功能",
    libraryTitle: "选择您想要的短视频类型",
    libraryIntro: "每种类型都有自己的脚本结构 — 从下方 4 种中选择。",
    detailBreadcrumbLibrary: "短视频模板",
    ctaSeeAll: "查看全部",
    ctaUseTemplate: "使用此模板 →",
    ctaUseTemplateRegulated: "我已了解 — 使用此模板",
    ctaCardSeeDetail: "查看详情 →",
    unboxing: {
      name: "开箱",
      shortDesc: "展示实物 · 建立信任",
      useWhen: "新品到货,需要建立可信度,包装精美",
      step1: "引人好奇的开箱钩子",
      step2: "产品展示与细节",
      step3: "手持时的亮点",
      step4: "第一印象",
      step5: "轻量 CTA",
    },
    flashSale: {
      name: "限时促销",
      shortDesc: "促进快速购买 · 紧急优惠",
      useWhen: "促销活动,清仓,限时优惠",
      step1: "震撼价格钩子",
      step2: "产品 + 具体优惠",
      step3: "立即购买的理由(紧迫感)",
      step4: "强力 CTA — 立即下单",
    },
    review: {
      name: "测评",
      shortDesc: "真实评价 · 体验分享",
      useWhen: "通过体验说服,多个卖点",
      step1: "提出客户痛点的钩子",
      step2: "产品作为解决方案",
      step3: "2-3 个主要优势",
      step4: "真实体验",
      step5: "CTA",
    },
    beforeAfter: {
      name: "前后对比",
      shortDesc: "结果对比 · 美妆",
      useWhen: "可见效果的产品(化妆品、保健品)",
      step1: "\"之前\"的状况",
      step2: "产品介绍",
      step3: "使用过程",
      step4: "\"之后\"的结果",
      step5: "CTA",
    },
    audience: {
      pickerLabel: "受众",
      pickerHint: "(选 1)",
      general: { name: "通用", tone: "中性、易于接受" },
      genZ: { name: "Z 世代 (16–24)", tone: "潮流、随意语言" },
      office: { name: "上班族 (25–35)", tone: "繁忙 — 强调便利性 + 质量" },
      mom: { name: "新手妈妈", tone: "注重安全,为孩子 / 家庭" },
      male: { name: "男性顾客", tone: "直接,少花哨" },
      mature: { name: "中年 (40+)", tone: "清晰、可信、无俚语" },
    },
    complianceChip: "⚠ 需审核",
    complianceCardTitle: "化妆品 / 保健品广告须知",
    complianceCardBody: "此类短视频可能违反越南第 181/2013/NĐ-CP 号法令。iSale 将在渲染前检查脚本 — 如果出现高风险功效声明,我们会建议修改或要求资质证明。",
    complianceSeeRules: "查看完整规定",
    metaDuration: "建议时长",
    metaRender: "渲染模板",
    metaSubtitle: "字幕",
    scriptStructureTitle: "脚本结构",
    descriptionTitle: "此模板的作用",
    emptyTitle: "无法加载模板",
    emptyBody: "连接似乎较慢。稍后将重试 — 或点击下方立即尝试。",
    emptyRetry: "重试",
    emptyFallback: "不使用模板创建短视频",
    contextChip: "模板:{{type}} · {{audience}}",
    contextChipRemove: "移除模板",
  },
  factsMobile: {
    // GA-M-05 a11y labels (2026-06-05)
    a11yVerify: "确认",
    a11yUnverify: "取消确认",
    a11yEdit: "编辑",
    a11yDelete: "删除",
    pullToRefresh: "下拉刷新",
    loadingLabel: "正在加载事实...",
    sourceVision: "AI 识别",
    sourceUserInput: "你添加",
    sourceVerified: "已验证",
    regulatedChip: "⚠ 敏感",
    riskyAlertTitle: "{{n}} 个高风险声明",
    riskyAlertDetails: "查看详情 →",
    riskySheetTitle: "需要资质证明的声明",
    riskySheetSubtitle: "越南广告法规 · 化妆品 / 保健品",
    riskySheetBody: "iSale 检测到此产品中的 {{n}} 个声明属于监管类别。如有资质证明，请勾选 — iSale 将降低脚本中的警告。",
    riskySheetAckLabel: "我有资质证明",
    riskySheetCtaSave: "保存确认",
    riskySheetCtaLater: "稍后",
    riskySheetInfoTitle: "📚 了解更多",
    riskySheetInfoLine1: "监管声明仍会生成 — iSale 在字幕中添加免责声明",
    riskySheetInfoLine2: "如无证明，建议删除以避免法律风险",
    saveShortcutHint: "(⌘S)",
    editSheetTitle: "编辑事实",
    createSheetTitle: "添加事实",
    sheetValueLabel: "内容",
    sheetCharCount: "{{n}} / 200",
    sheetCancel: "取消",
    sheetSave: "保存",
    sheetDelete: "🗑 删除此事实",
    sheetDeleteConfirmTitle: "删除此事实？",
    sheetSourceInfo: "编辑后,来源会变为「您手动输入」 — 请再次确认以用于脚本",
    addedToast: "已添加事实",
    deletedToast: "已删除事实",
    saveFailedToast: "保存失败 · {{err}}",
    deleteFailedToast: "删除失败 · {{err}}",
    emptyStateTitle: "暂无事实",
    emptyStateBody:
      "返回产品详情并点击\"AI 分析\",让 iSale 读取照片并推荐事实。或在下方手动添加。",
    inputPlaceholder: "如:SPF50+、不油腻、≤ 60 字符",
    riskyAccessibilityHint: "打开列出受规管声明的面板,可标记已有认证",
    /** FL-5 / GA-M-05: a11y label for the risky-claims banner (replaces hardcoded VN). */
    riskyAccessibilityLabel: "{{n}} 个高风险声明，双击查看详情并确认您有相关文件",
    saveChangesCta: "保存修改",
    addCta: "添加事实",
    editSourceHint: "修改后,来源会变为「您手动输入」 — 请再次确认以用于脚本生成。",
    createSourceHint: "该信息将以「您手动输入」为来源保存。如确认请验证。",
    fieldLabel: "字段：{{field}}",
    addFailed: "添加失败 · {{error}}",
  },
  factsWeb: {
    // GA-W-07 hardcode sweep (2026-06-05)
    emptyGroup: "— 暂无信息 —",
    title: "确认事实",
    breadcrumb: "确认事实",
    introBody: "在 iSale 生成脚本前请确认下方每个事实。这是防止 AI 编造的最后一道防线。",
    progressDone: "已确认 {{n}}/{{total}}",
    verifiedPercent: "{{p}}% · 还需确认 {{n}} 个事实",
    riskyWarningCount: "{{n}} 个高风险声明",
    riskyWarningDetails: "查看详情",
    ctaCreateScript: "创建短片 →",
    ctaCreateScriptNeeded: "还需确认 {{n}} 项",
    emptyTitle: "产品尚未分析",
    emptyBody: "iSale 还没读取此产品的图片 — 返回产品页并点击 \"AI 分析\" 让我们提取事实。约 25 秒。",
    emptyCtaPrimary: "🧠 立即 AI 分析",
    emptyCtaBack: "← 返回",
    drawerEditTitle: "编辑事实",
    drawerCreateTitle: "添加事实",
    drawerSave: "保存",
    drawerSaveShortcut: "(⌘S)",
    drawerCancel: "取消",
    drawerValueLabel: "内容",
    drawerValueMaxHint: "最多 200 字",
    drawerCharCount: "{{n}} / 200",
    drawerDelete: "删除此事实",
    deleteConfirmTitle: "删除此事实？",
    deleteConfirmAction: "删除",
    deleteConfirmCancel: "取消",
    addFactCta: "+ 添加{{field}}",
    riskyModalTitle: "需要资质证明的声明",
    riskyModalSubtitle: "越南广告法规 · 化妆品 / 保健品",
    riskyModalBody: "iSale 检测到此产品中的声明属于越南第 181/2013/NĐ-CP 号法令监管类别。如有资质证明，请勾选 — iSale 将降低脚本中的警告。",
    riskyModalAckLabel: "我有资质证明",
    riskyModalGroupLabel: "类别：{{group}}",
    riskyModalCtaSave: "保存确认",
    riskyModalCtaLater: "稍后",
    riskyModalInfoTitle: "📚 了解更多",
    sourceLabel: "{{source}} · 置信度 {{c}}%",
    factVerifiedLabel: "已确认 · {{c}}%",
    factPendingLabel: "待确认 · {{c}}%",
    factRiskyLabel: "⚠ 监管声明 · 需要资质证明",
    helpTopic: "facts-web",
    loadingLabel: "正在加载事实...",
    loadError: "无法加载事实 — 请检查网络",
    retryAction: "重试",
    saveFailedToast: "保存失败",
    deleteFailedToast: "删除失败",
    createScriptFailed: "无法创建脚本 · 请重试",
    productNotReady: "产品尚未就绪 — 请等待 AI 分析完成后再试",
  },
  render: {
    title: "渲染",
    chooseMode: "选择渲染模式",
    chooseModeTitle: "预览还是最终?",
    fastPreview: "快速预览",
    fastPreviewNote: "约 60-90 秒 · iSale 水印 · 720p · 不占额度",
    finalRender: "最终渲染",
    finalRenderNote: "约 3 分钟 · 1080×1920 · 按套餐水印 · 占 1 个视频",
    pipelineLabel: "AI 处理流程",
    pipelineHint: "您可以去做别的事 — 完成时我们会通知。",
    stageScript: "生成脚本和分镜",
    stageTts: "语音合成",
    stageCompose: "合成视频",
    stageExport: "导出 MP4 9:16",
    stageRunning: "运行中",
    longLoadingTitle: "下次的建议",
    longLoadingTip1: "输入图片少于 5 张",
    longLoadingTip2: "如不需要可关闭 AI 主镜头",
    longLoadingTip3: "在低峰时段渲染(23 点后)",
    errorTitle: "出错了",
    errorEmpathy: "不是您的问题 — 我们会免费重试,不扣额度。",
    errorRecommend: "建议",
    errorBackHome: "稍后 · 返回首页",
    // REM-016 parity fix — render.tsx keys missing from the mobile `render`
    // namespace (were rendering as literal key-paths). Mirrored from renderWeb.
    initJob: "准备中…",
    pollingHint:
      "iSale 正在处理 — 可以关闭应用,完成时会推送通知。",
    etaRemaining: "~还剩 {{seconds}} 秒",
    tipCloseApp: "💡 提示:可以关闭应用,完成时我们会通知你。",
    stepLabel: "步骤:{{step}}",
    stepFallback: "初始化",
    doneTitle: "渲染完成",
    doneBody: "点击「下一步」导出 MP4 + 缩略图。",
    cancelledTitle: "已取消",
    detailsTitle: "技术详情",
    detailsToggleShow: "查看技术详情",
    detailsToggleHide: "隐藏详情",
    rowId: "渲染编号",
    rowType: "类型",
    rowStatus: "状态",
    rowProgress: "进度",
    rowStep: "步骤",
    rowWatermark: "水印",
    rowCdnUrl: "CDN URL",
    watermarkYes: "是",
    watermarkNo: "否",
    fallbackDash: "—",
    cancelCta: "取消渲染",
    successToast: "渲染完成 · 视频已就绪 🎉",
    previewQueueFailed: "无法排队预览 · {{error}}",
    finalQueueFailed: "无法排队最终渲染 · {{error}}",
    // CF-H2: render reached before the clip is set up (no scene plan yet)
    preflightNoScenePlanTitle: "尚未设置短视频",
    preflightNoScenePlanBody:
      "渲染前请先选择模板、配音和音乐。",
    preflightSetupCta: "设置短视频",
    scenePlanRedirect: "尚未设置短视频 — 正在打开编辑器进行设置。",
    // CF-Nit: cancel toasts (localized; no false "quota refunded" claim)
    cancelledToast: "已取消渲染",
    cancelFailToast: "无法取消 · {{error}}",
    // CF-H4: client render timeout (taking longer than the worker ETA)
    stuckTitle: "比预期更久",
    stuckBody: "渲染时间异常偏长。请尝试刷新，或取消后重新渲染。",
    stuckRefreshCta: "刷新",
    openPreviewUrl: "打开预览链接",
    ctaNextExport: "下一步 → 导出",
    bugReportAck: "已收到错误报告 · iSale 团队将在 24 小时内回复",
    errorDetailLabel: "错误详情",
    errorSessionLine: "会话：{{session}} · {{time}} · 已记录给团队",
    fallbackTryOther: "尝试其他服务商",
    fallbackEta: "{{provider}} · 约 {{eta}} 秒",
    fallbackRetryLabel: "重试 {{label}}",
    fallbackRetryMeta: "约 50 秒 · 免费",
    bugReportTitle: "向 iSale 团队报告问题",
    bugReportMeta: "24 小时内回复",
  },
  modals: {
    upgradeTitle: "本月额度已用完",
    upgradeBody: "升级到入门版,每月再加 30 个视频。",
    upgradeCta: "升级 · 查看套餐",
    upgradeLater: "等下个月",
    costCapTitle: "已达 AI 费用上限",
    costCapBody: "本月 AI 预算已用完。升级即可继续创作。",
    costCapCta: "升级 · 查看套餐",
    costCapLater: "稍后",
    logoutTitle: "退出登录?",
    logoutBody: "您需要重新登录才能制作视频。",
    deleteAccountTitle: "永久删除账户",
    deleteAccountBody: "所有商品、视频、审计日志将在 30 天后删除。",
    deleteAccountConfirmPhrase: "永久删除我的账户",
    cancelRenderTitle: "取消渲染?",
    cancelRenderCta: "取消 · 不扣额度",
    cancelRenderWait: "继续等待",
    shareTitle: "分享视频",
    shareSubtitle: "打开原生分享面板分享短视频,或复制链接",
    shareTikTokSubtitle: "在 TikTok 中打开 MP4 + 文案",
    shareFacebookLabel: "Facebook",
    shareFacebookSubtitle: "分享到 Reels / Story",
    shareInstagramLabel: "Instagram",
    shareInstagramSubtitle: "分享到 Reels",
    shareYoutubeLabel: "YouTube Shorts",
    shareYoutubeSubtitle: "以 9:16 竖屏 Short 分享",
    shareShopeeLabel: "Shopee Video",
    shareShopeeSubtitle: "上传到店铺产品视频",
    shareCopyUrlLabel: "复制链接",
    shareSheetOpened: "已打开分享面板",
    shareSheetFailed: "无法打开分享面板",
    shareSuccess: "已分享视频",
    shareFailed: "分享失败",
    logoutCta: "退出登录",
    cancelRenderBodyTitle: "已到 {{pct}}% — 快完成了!",
    cancelRenderBodyDesc:
      "取消不会扣除额度（仅在渲染成功时计费）。如果渲染完成但您未立即观看，不会产生额外费用。",
    deleteProductTitle: "删除 {{name}}?",
    deleteProductBody: "照片、事实和所有相关短片都将被删除。无法撤销。",
    deleteProductConfirm: "永久删除",
    // GA-X-04: Archive (soft-delete) product
    archiveProductTitle: "归档 {{name}}?",
    archiveProductBody:
      "产品将从列表中隐藏。照片和事实仍会保留——您可以在设置中恢复。",
    archiveProductConfirm: "归档",
    archiveProductCancel: "取消",
    archiveProductSuccess: "产品已归档",
    archiveProductCta: "归档产品",
    voicePickerTitle: "选择配音",
    voicePickerSubtitle: "点击 ▶ 试听后再选择。",
    voicePickerUseCta: "使用此配音",
    voicePickerPreviewA11y: "试听 {{name}}",
    voicePickerProBadge: "Pro",
    complianceTitle: "几个表述需要确认",
    complianceBody:
      "以下句子可能违反广告政策。生成短片前请修改或删除。",
    complianceBackToFacts: "返回 Fact Locker 修改",
    complianceGuidance:
      "建议:使用宽泛措辞(「有助清洁」),避免绝对断言(「治愈 100%」)。",
    deleteAccountConfirmCta: "删除账户",
    deleteAccountUnlockCta: "请输入正确语句以解锁",
    deleteAccountTypePrompt: "请输入以下短语以确认:",
    deleteAccountTypePlaceholder: "输入确认短语…",
    deleteAccountRecoverHint:
      "删除后 30 天内,您仍可通过重新登录恢复账户。",
    tierBadgeCurrent: "当前方案",
    tierBadgePopular: "热门",
    tierVideosPerMonth: "{{n}} 条/月",
  },
  intl: {
    today: "今天",
    yesterday: "昨天",
    earlier: "更早",
  },
  tabs: {
    home: "首页",
    products: "商品",
    clips: "短片",
    account: "我",
  },
  onboarding: {
    skip: "跳过",
    continue: "继续",
    start: "开始",
    step: "第 {{n}}/{{total}} 步",
    valueTitle: "AI 销售短片\n3 分钟搞定",
    valueBody: "拍 3-5 张产品照片 — iSale 自动写脚本、配越南语配音、合成 9:16 短片,可发布到 TikTok、Reels、Shorts 与 Shopee Video。",
    painTitle: "CapCut 要 2-3 小时?\n这里只需 3 分钟。",
    painBody: "无需学习曲线。不需要剪辑技能。AI 负责脚本 + 配音 + 镜头合成 — 您只需审核 + 发布。",
    howTitle: "3 步 · 1 次操作",
    howBody: "拍照 → AI 分析并写脚本 → 您选版本就拿到 MP4。没有'连夜渲染'阶段。",
    trustTitle: "透明 + 安全",
    trustBody: "每次 AI 调用都有审计日志。化妆品/保健品/药品自动合规检查。您随时可删除数据。",
    notifTitle: "允许通知?",
    notifBody: "短片渲染完成时通知您(约 30 秒后!)。不发垃圾 — 只通知与您账户相关的事件。",
    notifAllow: "允许通知",
    notifLater: "稍后",
  },
  notificationsInbox: {
    title: "通知",
    markAllRead: "全部标为已读",
    empty: "暂无通知",
    badgeRenderDone: "已渲染",
    badgeRenderFailed: "渲染失败",
    badgeQuotaWarn: "额度",
    badgePaymentFailed: "付款",
    justNow: "刚刚",
    minutesAgo: "{{n}} 分钟前",
    hoursAgo: "{{n}} 小时前",
  },
  profileOnboarding: {
    menuRowTitle: "重新查看引导",
    menuRowNote: "5 步了解 iSale 工作方式",
    card1Title: "拍摄产品照片",
    card1Body: "1-5 张,JPG/PNG/WebP。iSale 自动审核,AI 读取并推荐卖点。",
    card2Title: "验证 Fact Locker",
    card2Body: "对 AI 找出的事实打 ✓。至少 3 项已验证才能解锁脚本生成 — 防止 AI 编造。",
    card3Title: "AI 写 3 个脚本",
    card3Body: "AI 生成钩子 + 场景 + CTA。您选 1 个,如需可修改。",
    card4Title: "编辑器 + 快速渲染",
    card4Body: "选模板 + 配音 + 音乐。预览渲染 60 秒,正片约 3 分钟。",
    card5Title: "下载 MP4 + 分享短视频",
    card5Body: "导出 1080×1920 + 文案 + 标签。发布到 TikTok、Reels、Shorts、Shopee Video。",
    startCta: "开始",
    nextCta: "下一步 →",
    skipCta: "跳过",
    stepLabel: "第 {{n}}/5 步",
    doneCta: "明白 · 关闭",
  },
  // Auth-expired modal — friendly UX replacing technical 401 toast.
  // Vincent 2026-05-23.
  auth: {
    sessionExpiredTitle: "登录会话已过期",
    sessionExpiredSubtitle: "为了账户安全,请重新登录以继续。",
    sessionExpiredBody:
      "为保护您的账户,iSale 在闲置一段时间后会要求重新登录。您正在输入的草稿已保存在本设备上。",
    sessionExpiredCountdown: "{{seconds}} 秒后自动跳转…",
    sessionExpiredLoginNowCta: "立即登录",
    sessionExpiredLaterCta: "稍后",
  },
  consent: {
    loading: "正在加载 AI 条款…",
    loadFailedTitle: "无法加载 AI 条款",
    loadFailedBody: "请检查网络后重试。",
    retryCta: "重试",
    saveFailed: "无法保存条款 · {{error}}",
    tryAgainLater: "请稍后重试",
    heroTitle: "iSale 使用 AI\n为您制作短片",
    heroSubtitle: "以下是我们对您数据的处理方式",
    sendsTitle: "我们发送给",
    sendsOnly: "仅发送:",
    sendsOnlyValue: "产品照片 + 您输入的信息",
    neverTitle: "绝不发送",
    neverValue: "邮箱 · 支付信息 · 其他工作区 · 个人照片",
    policyLink: "阅读完整 AI 透明度说明",
    versionFooter: "同意书 v{{version}} · 审计日志保留 {{days}} 天",
    acceptCta: "我同意 · 立即制作",
    accepting: "保存中…",
    // §6 B1 — reprompt modal strings, fired when backend returns 403
    // CONSENT_REQUIRED indicating the consent policy version changed.
    repromptTitle: "AI 条款已更新",
    repromptSubtitle: "版本 {{version}} — 请重新确认以继续使用 AI 功能。",
    repromptAcceptCta: "同意并继续",
    repromptViewCta: "查看详情",
    repromptWhatsNewTitle: "更新内容?",
    repromptWhatsNewBody:
      "新增语音生成存储规定 · 更新发送至 OpenAI 的数据类别 · 渲染短片最长保留 90 天(原 60 天)。",
  },
  helpTooltip: {
    acknowledgeCta: "知道了",
    reopenHint: "可在「设置」→「帮助」中随时重新打开此引导。",
    homeTitle: "首页 · 30 秒上手",
    homeStep1: "点击中间的大按钮制作新短片。",
    homeStep2: "配额区显示本月剩余视频数量。",
    homeStep3: "「最近短片」可重看已渲染的视频。",
    editorTitle: "Editor · 30 秒",
    editorStep1: "时间轴上的每个场景 = 短片的一段。",
    editorStep2: "点击图片 / 文字以编辑或调整顺序。",
    editorStep3: "「更换配音」按钮打开免费配音选择器。",
    factsTitle: "Fact Locker · 30 秒",
    factsStep1: "AI 根据照片和产品名推荐核心卖点。",
    factsStep2: "取消勾选您不同意的事实 — AI 不会用于生成脚本。",
    factsStep3: "被标记(黄色)的事实需在继续前修改或删除。",
    renderTitle: "渲染中 · 30 秒",
    renderStep1: "渲染需 1-3 分钟,取决于长度与质量。",
    renderStep2: "您可以关闭应用 — iSale 完成时会通知您。",
    renderStep3: "若渲染超过 5 分钟,可取消并免费重试。",
  },
  emptyStates: {
    productsTitle: "暂无商品",
    productsBody: "添加首个商品 — 拍几张照片,iSale 自动推荐卖点事实。",
    productsCta: "添加商品",
    clipsTitle: "暂无短片",
    clipsBody: "渲染完成后,您的短片会出现在这里以便查看和分享。",
    clipsCta: "制作首个短片",
    searchInitialTitle: "开始搜索",
    searchInitialBody: "输入商品、事实或短片名称,快速在工作区中查找。",
    searchNoResultsTitle: "没有「{{query}}」的结果",
    searchNoResultsBody: "尝试其他关键词或移除筛选条件以扩大结果。",
    notificationsTitle: "暂无通知",
    notificationsBody: "iSale 会在短片渲染完成或有新销售提示时通知您。",
    notificationsCta: "返回首页",
    aiLogTitle: "暂无 AI 活动",
    aiLogBody:
      "当 iSale 为您调用 AI(推断事实、撰写脚本、生成配音),记录会显示在这里。",
    aiLogCta: "制作首个短片",
  },
  emptyHome: {
    greeting: "新朋友您好,",
    name: "您 👋",
    title: "暂无短片",
    subtitle: "拍一张商品照片 — 3 分钟后就有卖货短片。",
    step1: "拍产品照片",
    step1Note: "3–5 个角度",
    step2: "确认 AI 推断的信息",
    step2Note: "约 30 秒",
    step3: "获取短片 · 发布到各平台",
    step3Note: "约 2 分钟",
    cta: "+ 制作第一个短片",
    watchVideo: "或观看 60 秒演示 ↗",
  },
  products: {
    // GA-M-05 hardcode sweep (2026-06-05)
    cameraPermissionError: "相机权限 · {{error}}",
    libraryPermissionError: "相册权限 · {{error}}",
    uploadError: "上传失败 · {{error}}",
    retryLaterHint: "请稍后重试",
    analyzeNeedImage: "需要至少 1 张通过审核的图片才能进行 AI 分析。",
    analyzeDone: "分析完成 · 新增 {{created}} 条（共 {{total}} 条）· {{flags}}",
    analyzeFailed: "分析失败 · {{error}}",
    retryHint: "请重试",
    title: "商品",
    heroTitle: "商品照片库",
    heroBody: "每个商品都有照片 + 已核实事实 → 随时生成短片。",
    emptyTitle: "暂无商品",
    emptyBody: "添加商品，让 AI 记住名称、价格和描述 — 随时重复使用。",
    emptyCta: "+ 添加商品",
    emptyCaptureCta: "或立即拍一张",
    searchPlaceholder: "搜索商品…",
    searchA11y: "搜索商品",
    statusReady: "就绪",
    statusDraft: "草稿",
    gridSubtitle: "{{count}} 个商品 · {{ready}} 个可生成短片",
    gridAddCta: "添加商品",
    noImage: "暂无图片",
    searchEmpty: "未找到匹配的商品",
    detailTitle: "商品",
    detailAnalyzeTitle: "AI 分析",
    detailAnalyzeBody: "AI 读图,提议卖点 + 目标受众 + 宣称。核实后再用于生成脚本(Fact Locker)。",
    detailAnalyzeCta: "立即分析",
    /** GA-M-13: moved from productsWeb namespace — used on both mobile + web. */
    detailCreateClipCta: "用此商品创建短片",
    detailVerifyFactsCta: "确认事实",
    detailAnalyzeBusy: "分析中…",
    detailAnalyzeCost: "免费(已缓存)",
    detailAssetsLabel: "照片",
    detailAssetsEmpty: "暂无照片。",
    detailEditTitle: "编辑名称与类别",
    detailEditBody: "修改商品名称和类别",
    newTitle: "新建商品",
    newStep1Kicker: "第 1 / 3 步",
    newStep1Title: "商品信息",
    newStep1Body: "使用短名称(例如 \"SPF50 防晒霜\")。类别决定应用哪些合规规则。",
    newNameLabel: "商品名称",
    newNamePlaceholder: "例如:SPF50 防晒霜",
    newCategoryLabel: "类别",
    newStep2Kicker: "第 2 / 3 步",
    newStep2Title: "商品照片",
    newStep2Body: "拍摄或选择 1-{{max}} 张照片。JPG/PNG/WebP,每张 ≤ 5MB。干净 + 明亮的照片帮助 AI 更准确地读取。",
    newFramingTipTitle: "将商品放在画面中央",
    newFramingTipBody: "3–5 个角度 · 干净的背景 · 充足的光线",
    newCameraCta: "拍摄",
    newLibraryCta: "从相册",
    newAssetsCount: "{{n}}/{{max}} 张照片",
    newAssetsEmpty: "暂无照片。使用上方按钮添加。",
    newSubmitCta: "创建商品 + 上传 {{n}} 张照片",
    newSubmitBusy: "正在创建商品…",
    newDoneTitle: "商品已创建",
    newDoneApproved: "通过",
    newDoneRejected: "被拒",
    newDoneFailed: "上传出错",
    newDoneRejectedHint: "{{n}} 张照片被审核拒绝。打开详情页面替换或删除。",
    newDoneDetail: "查看详情 + AI 分析",
    newDoneBack: "返回列表",
    catCosmetic: "美妆",
    catSupplement: "保健品",
    catFashion: "时装",
    catFood: "食品",
    catElectronics: "电子产品",
    catHome: "家居用品",
    catOther: "其他",
    uploadInitializing: "正在初始化…",
    uploadHashing: "正在准备照片 {{index}}/{{n}}",
    uploadConnecting: "正在连接服务器…",
    uploadUploading: "上传照片 {{index}}/{{n}} · {{pct}}%",
    uploadConfirming: "AI 正在分析 {{n}} 张照片…剩约 {{etaS}}s",
    uploadFailed: "照片 {{index}} 失败:{{error}}",
    uploadProcessing: "正在处理图片…",
    validateNameLen: "名称需 2-200 字符",
    editSaveSuccess: "已保存修改",
    editDeleteSuccess: "已删除商品",
    detailFallbackName: "此商品",
    editTitle: "编辑商品",
    editDirty: "未保存",
    editSaveCta: "保存更改",
    editSaveBusy: "保存中…",
    editSaveNoChange: "无更改",
    editDangerTitle: "危险操作",
    editDangerCard: "删除商品",
    editDangerBody: "照片、事实和相关短片会被归档(30 天软删除)。可在设置中恢复。",
    editDangerCta: "删除此商品",
  },
  projects: {
    listTitle: "短片",
    listHeroTitle: "您的短片",
    listHeroBody: "您渲染过的所有短片 — 分享短视频、重新下载 MP4 或使用不同模板/配音再次渲染。",
    listLoading: "加载短片…",
    listEmptyTitle: "暂无短片",
    listEmptyBody: "制作第一个短片 — AI 会学习您喜欢的风格。",
    filterAll: "全部",
    filterA11y: "筛选短片",
    searchPlaceholder: "搜索短片…",
    searchA11y: "搜索短片",
    filterInProgress: "进行中",
    sectionInProgress: "进行中",
    sectionDone: "已完成",
    sectionArchived: "已归档",
    stepScript: "脚本",
    stepRender: "渲染",
    stepExport: "导出",
    continueCta: "继续 →",
    filterEmpty: "没有此状态的短片",
    listEmptyCta: "+ 制作短片",
    newTitle: "新短片",
    newKicker: "生成脚本前的最后一步",
    newHero: "命名 + 选择时长",
    newBody: "AI 会按照风格 + 时长写出 3 个脚本版本。",
    newNameLabel: "短片名称",
    newNamePlaceholder: "例如:SPF50 早晨例行展示",
    newDurationLabel: "时长(秒)",
    newToneLabel: "脚本风格",
    newSubmitCta: "创建 + 生成 3 个脚本",
    newSubmitBusy: "正在创建 + 生成…",
    newCostHint: "AI 生成 3 个脚本版本 · 约 10 秒",
    toneCasual: "亲切",
    toneProfessional: "专业",
    tonePlayful: "活泼",
    toneTrustworthy: "可靠",
    validateNameLen: "名称需 2-200 字符",
    scriptTitle: "脚本",
    scriptEmptyTitle: "暂无脚本",
    scriptEmptyBody: "AI 将根据已验证的 Facts 生成 3 个脚本版本，选择最合适的一个继续。",
    scriptGenerateCta: "用 AI 生成脚本",
    scriptGenerating: "AI 正在撰写脚本…",
    scriptGeneratingBody: "大约需要 10–20 秒，请勿关闭 App。",
    scriptGenerateFail: "脚本生成失败，请重试。",
    scriptLoading: "加载脚本…",
    scriptComplianceWarn: "{{n}} 条合规警告",
    scriptComplianceBody: "请仔细阅读下方被标记的台词;如有违规请点击 \"重新生成\" 并给出具体反馈。",
    scriptRegenCta: "带反馈重新生成…",
    scriptRegenTitle: "下一次生成的反馈",
    scriptRegenBody: "例如:\"更活泼\"、\"缩短到 20 秒\"、\"不要用'美白'\"。",
    scriptRegenPlaceholder: "留空 = 随机重新生成",
    scriptRegenSubmit: "重新生成 3 个版本",
    scriptRegenBusy: "重新生成脚本…",
    scriptSelectBusy: "保存中…",
    scriptSelectActive: "已选 ✓",
    scriptSelectCta: "选择此版本",
    scriptCtaPick: "选择一个版本继续",
    scriptCtaNext: "下一步 → Editor + Render",
    scriptCaptionBlock: "Caption + 标签(自动)",
    scriptCaptionLoading: "生成 caption…",
    scriptCaptionEmpty: "选择版本后才会出现 caption。",
    scriptSelectedBadge: "★ 已选",
    scriptToneEnergetic: "活力",
    scriptToneRefined: "精致",
    scriptToneFun: "趣味",
    scriptSelectFailed: "选择失败 · {{error}}",
    scriptRegenSuccess: "已生成 3 个新版本",
    scriptRegenFailed: "重新生成失败 · {{error}}",
    scriptVariantMeta: "{{seconds}}秒 · {{chars}} 字",
    scriptMoreScenes: "+ 还有 {{n}} 个场景",
  },
  editor: {
    title: "Editor Lite",
    scenePillSummary: "{{scenes}} 场 · {{seconds}}s",
    tabTemplate: "模板",
    tabVoice: "配音",
    tabMusic: "音乐",
    tabSubtitle: "字幕",
    templateBody: "9:16 竖屏,适配各短视频平台",
    voiceCount: "{{n}} 个越南语 Neural 配音",
    voiceWarn: "⚠ 更改配音 = 全部场景重新 TTS(新 POST)。",
    musicCount: "{{n}} 条已授权曲目",
    musicNone: "(无背景音乐)",
    musicNoneSub: "仅配音",
    subtitleBody: "字幕样式(推荐:Noto Sans VN)",
    engineLabel: "渲染引擎",
    engineFast: "Fast Mode",
    engineFastNote: "免费 · 约 3 秒渲染 · Ken Burns + TTS",
    engineAi: "AI Mode",
    engineAiNote: "AI 主镜头 · Phase 2 PRO",
    planSummary: "当前分镜",
    planSummaryBody: "{{scenes}} 个镜头 · 总 {{seconds}} 秒 · {{cacheHits}} 个缓存命中(免费)",
    saveCta: "创建分镜 + 渲染 →",
    saveUpdateCta: "保存更改 + 渲染 →",
    saveBusy: "保存分镜…",
    missingInfo: "渲染前请选择模板 + 配音。",
    subtitleStyleWhite: "基础白色",
    subtitleStyleYellow: "醒目黄色",
    voiceGenderFemale: "女声",
    voiceGenderMale: "男声",
    voicePreviewA11y: "打开语音试听",
    voicePreviewCta: "试听",
    voicePreviewPreparing: "正在准备试听 {{name}}…",
  },
  export: {
    title: "导出 + 分享",
    heroTitle: "短片已就绪 🎉",
    heroPreviewLabel: "9:16 预览",
    heroMeta: "MP4 · 9:16 · session {{session}}",
    readyKicker: "准备发布",
    readyTitle: "下载 + 分享",
    watermarkTitle: "iSale 水印已开启",
    watermarkBody: "免费版渲染都带水印。升级 Pro 去除。",
    captionLabel: "Caption + 标签",
    platformLabel: "目标平台",
    platformHint: "话题标签按平台自动调整 — 不重新生成 AI",
    copyForPrefix: "复制到",
    captionLoading: "生成文案 + 标签…",
    captionEmpty: "—",
    formatsLabel: "格式",
    formatMp4: "MP4",
    formatThumbnail: "Thumbnail",
    formatSrt: "SRT",
    formatAll: "全部",
    existingLabel: "已导出({{n}})",
    existingEmpty: "点击上方按钮导出。",
    shareCta: "分享短视频",
    shareHint: "打开原生分享面板 · caption + 标签 + URL 预填",
    actionView: "预览",
    actionEdit: "编辑",
    actionRerender: "重新渲染",
    actionExport: "导出",
  },
  aiActivity: {
    title: "AI 活动日志",
    intro: "我们为您发起的每一次 AI 调用都已记录 — 透明且您可申请删除。",
    filterAll: "全部",
    loading: "加载审计日志…",
    emptyAll: "暂无审计日志。",
    emptyProvider: "{{provider}} 还没有调用记录。",
    sectionToday: "今天",
    sectionYesterday: "昨天",
    sectionEarlier: "更早",
    capAnalyzeImage: "图像分析",
    capGenerateScript: "生成脚本",
    capGenerateCaption: "Caption + 标签",
    capCheckCompliance: "合规检查",
    capSynthesizeVoice: "配音",
    capGenerateVideo: "视频合成",
    rowProviderLabel: "AI 系统:",
    rowSessionLabel: "session",
    rowTokensUnit: "tokens",
    rowLatencyUnit: "ms",
    loadMore: "加载更多",
    loadingMore: "加载中…",
    showingOf: "显示 {{shown}} / {{total}} 条",
  },
  profileEdit: {
    title: "编辑资料",
    dirty: "未保存",
    changeAvatar: "更换头像",
    avatarHint: "即将推出 — 在后续更新中可更换头像。",
    nameLabel: "显示名称",
    namePlaceholder: "您在 app 内显示的名称",
    emailLabel: "邮箱(只读)",
    emailHint: "更改邮箱 = 使用其他账户登录。",
    localeLabel: "语言",
    localeHint: "保存也会同步到 Stripe checkout + 审计日志。",
    saveCta: "保存更改",
    saveShort: "保存",
    saveBusy: "保存中…",
    saveNoChange: "无更改",
    loading: "加载资料…",
    loadError: "无法加载资料",
    saved: "已保存资料",
  },
  themePicker: {
    title: "外观",
    intro: "选择主题模式。\"自动\" 会跟随操作系统 — 白天浅色,iOS / Android 切换深色时跟着切换。",
    lightLabel: "浅色模式",
    lightDesc: "白色背景 · 深色文字 · 白天使用",
    darkLabel: "深色模式",
    darkDesc: "深色背景 · 浅色文字 · 夜间护眼",
    systemLabel: "跟随系统",
    systemDesc: "主题跟随系统 · vi/en/zh 均支持",
    betaKicker: "BETA",
    betaBody: "部分旧屏幕暂未跟随主题切换。我们正在逐步完善,让整个应用保持一致。",
    toastSystem: "跟随系统",
    toastDark: "深色模式",
    toastLight: "浅色模式",
  },
  languagePicker: {
    // GA-M-05 sweep (2026-06-05)
    syncFailed: "已在本机切换语言；尚未同步到服务器（{{error}}）",
    title: "语言",
    intro: "选择显示语言。越南语为默认。英语和中文均已完整翻译。",
    parityLabel: "Glossary 一致性",
    parityBody: "所有 key 都有 3 种语言版本。货币按 locale 自动切换 VND ↔ USD ↔ CNY。日期格式遵循 Intl API。",
    restartTitle: "需要重启 App",
    restartToZh: "切换到中文需要重新加载原生模块。",
    restartFromZh: "切换离开中文需要重新加载原生模块。",
    restartCta: "更改并提示我关闭 app",
    restartConfirmed: "语言已更改 · 请关闭并重新打开 app 以完成切换",
    toastSwitched: "已切换到 {{name}}",
    nativeViet: "Xin chào!",
    nativeEn: "Hello!",
    nativeZh: "你好!",
    requiresRestartSuffix: "· 需要重启 app",
  },
  dashboard: {
    kicker: "概览",
    newClipCta: "新建短片",
    quotaLabel: "本月",
    quotaRemaining: "套餐 {{plan}} · 还剩 {{n}} 条",
    upgradePro: "升级到 Pro →",
    backendDown: "无法连接 iSale 服务器。请检查网络后重新加载。",
    tipsKicker: "小贴士",
    tipsBody:
      "每个商品应有 3-5 张高质量照片 + 在生成脚本前核实 ≥ 3 个 facts。facts 经核实后 AI 写出的脚本更准确。",
    emptyProducts: "暂无商品 — 前往「商品」创建。",
    emptyClips: "暂无短片 — 前往「短片」生成。",
    openLink: "打开",
    seeAll: "查看全部 →",
  },
  notifications: {
    bellLabel: "通知",
    drawerTitle: "通知 · {{count}} 条新",
    readAll: "全部已读",
    loading: "加载中…",
    loadError: "无法加载通知",
    emptyTitle: "收件箱为空",
    emptyBody: "当 clip 渲染完成或有更新时, 会在这里提醒你",
    relativeNow: "刚刚",
    relativeMin: "{{n}} 分钟",
    relativeHour: "{{n}} 小时",
    relativeDay: "{{n}} 天",
  },
  upload: {
    /** Screen-reader-only moderation badge labels — see vi.ts comment. */
    phase_approved: "图片已批准",
    phase_pending: "图片待审核",
    phase_rejected: "图片被拒绝",
    idleTitle: "拖放产品图片到这里",
    idleSubline: "或点击从本机选择 · JPG / PNG / WebP · 最多 10 张",
    dragHere: "将图片放到这里",
    rejectTitle: "不支持此文件",
    rejectSubline: "文件 '{{file}}' 被拒绝 · {{reason}}",
    reasonType: "格式不支持",
    reasonSize: "超过 5MB 上限",
    hintClick: "点击选择文件",
    hintPaste: "Cmd+V 粘贴",
    hintFolder: "拖入文件夹",
    or: "或",
    pickFromDevice: "从本机选择",
    pickFolder: "上传文件夹",
    pasteCta: "从剪贴板粘贴",
    tipKicker: "提示",
    tipBody: "拍 3-5 张多角度图片可让 AI 分析更准确",
    tooManyFiles: "选了 {{selected}} 张但只剩 {{room}} 个位置 · 多余的被跳过",
    // Generic — raw {{error}} removed.
    prepareFail: "无法准备图片,请重试。",
    preparing: "正在准备图片…",
    headerCount: "{{count}}/10 张 · {{size}}",
    headerMax: "{{count}}/10 张 · {{size}} · 已达上限",
    headerUploading: "正在上传 {{done}}/{{total}} · 总进度 {{pct}}%",
    clearAll: "清空全部",
    continue: "继续",
    cancelAll: "全部取消",
    addMore: "添加",
    maxTip: "提示: 5-7 张通常够 AI 分析了, 不必到 10 张",
    errorTitle: "{{count}} 张图片上传失败",
    errorGeneric: "上传失败",
    retryAll: "全部重试",
    removeFile: "移除",
    rejectedOpen: "查看审核详情",
    rejectedChip: "违反政策",
    moderating: "审核中",
    confirmTitle: "确认上传",
    confirmStart: "开始上传",
    confirmSummary: "{{count}} 张 · {{size}} · 准备发送",
    confirmAiHint: "上传后 AI 自动分析 — 你可以在生成脚本前调整",
    kbdHint: "按 Esc 取消 · Enter 确认",
    cancelTitle: "取消上传?",
    cancelKeepGoing: "继续上传",
    cancelDoIt: "取消上传",
    cancelBody: "{{done}}/{{total}} 已上传. 取消会丢弃所有已上传文件并返回拖放区",
    pasteTitle: "从剪贴板粘贴图片",
    pasteAdd: "加入列表",
    // Generic — 401 handled by AuthExpiredModal.
    urlsFail: "无法准备上传。请检查网络后重试。",
    completeTitle: "上传完成!",
    completeBody: "{{count}} 张图片已就绪. AI 可以开始分析",
    completeRejectedNote: "{{count}} 张被审核拒绝, 未计入批次",
    completeEta: "分析约需 15 秒 · 不计入配额",
    // Renamed — see vi.ts comment.
    completeAnalyze: "完成,查看商品",
    completeAddMore: "上传更多图片",
    rejectedAllTitle: "所有图片均被拒绝",
    rejectedAllBody: "至少需要 1 张图片通过审核, AI 才能分析产品",
    rejectedAllReasonLabel: "审核原因",
    rejectedAllHint: "尝试更清晰的产品照片 · 避免包含人脸或不适合内容的照片",
    rejectedAllRetry: "上传其他图片",
    moderationDrawerTitle: "图片未通过审核",
    moderationReasonLabel: "原因",
    moderationDefaultReason: "AI 检测到不合规内容",
    moderationPolicyLink: "查看审核政策",
    moderationDelete: "移除图片",
    // Friendly reject-reason mapping — see vi.ts for full context.
    rejectReasons: {
      systemLicense_title: "图片等待审核",
      systemLicense_body: "本环境上的 AI 审核模型尚未完全启用。这是系统问题,与您上传的图片无关。",
      systemLicense_hint: "请稍后重试,或联系管理员。",
      systemAi_title: "审核服务暂时中断",
      systemAi_body: "AI 审核服务暂时无法正确响应。您的图片没有问题。",
      systemAi_hint: "请几分钟后重试。",
      systemR2_title: "无法从服务器读取图片",
      systemR2_body: "无法从存储中读回已上传的图片。通常是上传未完成或网络问题。",
      systemR2_hint: "请重新上传图片。",
      unparseable_title: "审核结果无效",
      unparseable_body: "AI 返回的结果格式不正确。这是系统问题。",
      unparseable_hint: "请重试。",
      abandoned_title: "上传被中断",
      abandoned_body: "之前的上传未完成(应用/浏览器过早关闭)。",
      abandoned_hint: "请重新上传图片。",
      size_title: "图片太大",
      size_body: "图片超过允许的大小限制(最大 10MB)。",
      size_hint: "请压缩或选择更小的文件。",
      content_title: "内容不适合",
      content_body: "{{detail}}",
      content_hint: "请使用其他产品图片。避免敏感内容。",
      unknown_title: "已拒绝",
      unknown_body: "AI 未提供具体原因。",
      technicalDetailLabel: "技术详情",
      technicalDetailHint: "供开发 / 支持升级使用",
      groupCountSuffix: "{{count}} 张图片",
    },
  },
  productsWeb: {
    createCta: "新建商品",
    uploadFor: "上传产品图片",
    createTitle: "新建商品",
    createSubmit: "创建",
    createBusy: "创建产品中…",
    createFailPrefix: "无法创建:",
    uploadHint:
      "请在移动 App 中上传照片 — 网页版暂不支持拖拽。",
    tableName: "名称",
    tableCategory: "类别",
    tableStatus: "状态",
    tableUpdated: "更新时间",
    // GA-W-12: was `emptyHintMobile` — renamed
    emptyHint:
      "点击「新建商品」开始,或在移动 App 上传照片 + facts。",
    cardCategory: "类别",
    cardUpdated: "更新于 {{date}}",
    cardNoCover: "暂无图片",
    cardOpen: "打开",
    detailBackToList: "返回商品",
    detailBreadcrumb: "商品",
    detailAssetsTitle: "商品图片",
    detailAssetsEmpty: "暂无图片 — 在下方上传以开始。",
    detailUploadTitle: "上传图片",
    detailUploadMore: "上传更多",
    detailCreateClipCta: "用此商品创建短片",
    detailVerifyFactsCta: "确认事实",
    detailNotReady: "需要 3 张已审核图片才能创建短片。",
    detailLoading: "加载商品中…",
    detailLoadError: "商品加载失败",
    detailRetry: "重试",
    detailAuthErrorTitle: "会话已过期",
    detailAuthErrorCta: "重新登录",
    detailJoinedAt: "创建于 {{date}}",
    detailAssetCountReady: "{{n}} 张就绪",
    detailAssetCountPending: "{{n}} 张待审核",
    detailAssetCountRejected: "{{n}} 张被拒绝",
    // EP-7: template-context banner on product list
    templateBannerUsing: "已选模板:",
    templateBannerPickHint: "在下方选择产品，开始使用此模板创建短视频。",
    templateBannerClear: "取消模板",
  },
  projectsWeb: {
    // GA-W-12: was `mobileHint` — renamed; was `emptyHintMobile` — renamed
    featureHint:
      "编辑脚本、切换配音/音乐/模板、渲染预览或最终版,并导出 MP4 / SRT / caption 文件。",
    emptyHint: "暂无短片。点击「新建短片」从已就绪的商品开始创建。",
    cardOpen: "打开",
    cardCreated: "创建于 {{date}}",
    newCta: "新建短片",
    newModalTitle: "新建短片",
    newProductLabel: "选择商品",
    newProductPlaceholder: "— 选择已就绪商品 —",
    newNoReadyProducts: "暂无「就绪」状态的商品。请先上传图片 + 验证 facts。",
    newSubmitting: "创建短片中…",
    newSubmitFail: "创建失败 · {{error}}",
    newSubmitHint: "选择商品并输入名称以创建",
    goToProducts: "前往商品",
  },
  billingWeb: {
    currentLabel: "使用中",
    tierName: "{{name}} 套餐",
    resetCost: "重置于 {{date}} · 成本 ${{used}} / ${{cap}}",
    starterFeature1: "每月 30 个视频",
    starterFeature2: "无水印",
    starterFeature3: "5 个越南语高级配音",
    proFeature1: "每月 150 个视频",
    proFeature2: "AI 主镜头 (Kling)",
    proFeature3: "品牌套件 + 自定义模板",
    enterpriseFeature1: "无限视频",
    enterpriseFeature2: "API 访问 · SLA",
    enterpriseFeature3: "团队工作区",
    enterpriseTagline: "面向团队和大型店铺",
    enterprisePrice: "联系我们",
    perMonth: "/月",
    perYear: "/年",
    checkoutFail: "无法创建结账:",
    freeFeature1: "每月 3 个短视频",
    freeFeature2: "Fast Mode 渲染(无 Kling)",
    freeFeature3: "iSale 水印",
    lifetimeFeature1: "每月 30 个短视频",
    lifetimeFeature2: "AI 模式 (Kling i2v):每月 3 个",
    lifetimeFeature3: "优先支持 + 测试功能",
    lifetimeTagline: "一次付费,永久使用",
    lifetimePeriod: "一次性",
    lifetimePrice: "2,990,000₫",
    emailIntentTitle: "早期注册终身版",
    emailIntentSubtitle: "限 200 人 · 开放付款后我们会联系您。",
    emailIntentEmailPlaceholder: "您的邮箱@example.com",
    emailIntentEmailLabel: "电子邮箱",
    emailIntentCta: "注册",
    emailIntentSuccess: "已记录！我们会尽快联系您。",
    emailIntentError: "发送失败 — 请重试。",
    emailIntentInvalid: "邮箱格式不正确。",
  },
  aiActivityWeb: {
    heroTitle: "我们为您做了什么 (全程留痕)",
    introFull:
      "每次 AI 帮您工作时 (识图、写脚本、合成语音) — 我们都记录在这里供您审查。除下方列出的 AI 提供商外,没人能看到您的短片或产品。日志 90 天后自动清除。",
    pillSummary: "{{n}} 次 AI 操作",
    tableWhen: "时间",
    tableCapability: "能力",
    tableProviderModel: "AI 系统(模型)",
    tableCost: "成本",
    tableLatency: "处理速度 (ms)",
    tableStatus: "状态",
  },
  projectDetailWeb: {
    loadingProject: "加载短视频…",
    notFoundTitle: "未找到短视频",
    backToClips: "返回短片列表",
    backAllClips: "全部短片",
    pillLabel: "短片",
    productLabel: "商品:",
    productNone: "—",
    versionPill: "v{{version}}",
    emptyScriptTitle: "暂无脚本",
    emptyScriptBody:
      "AI 将根据已验证的 Facts 生成 3 个脚本版本，选择最合适的一个继续。",
    generateScriptCta: "用 AI 生成脚本",
    generatingScript: "AI 正在撰写脚本…",
    generatingScriptBody: "大约需要 10–20 秒，请勿关闭页面。",
    generateScriptFail: "脚本生成失败，请重试。",
    complianceWarnCount: "{{n}} 条合规警告",
    complianceWarnBody:
      "请仔细阅读下方被标记的台词;如有违规请点击「重新生成」并给出具体反馈。",
    providerFallback: "未知",
    modelFallback: "—",
    selectFailAlert: "无法选中该版本。",
    regenFailAlert: "无法重新生成。",
    variantLabel: "版本 {{n}}",
    variantMeta: "{{scenes}} 个镜头 · {{seconds}}s",
    variantMoreScenes: "+ 还有 {{n}} 个镜头",
    variantCtaLabel: "CTA",
    captionTitle: "Caption + 标签(自动)",
    captionLoading: "生成 caption…",
    captionEmpty: "选择版本后才会出现 caption。",
    regenCta: "带反馈重新生成",
    regenBusy: "重新生成中…",
    ctaPick: "选择一个版本继续",
    ctaNext: "下一步 → Editor Lite",
    variantSelected: "已选 ✓",
    variantSelect: "选择此版本",
    variantSaveBusy: "保存中…",
    modalTitle: "下一次生成的反馈",
    modalBody: "例如:「更活泼」、「缩短到 20 秒」、「不要用'美白'」。",
    modalPlaceholder: "留空 = 随机重新生成",
    modalSubmit: "重新生成 3 个版本",
    modalClose: "关闭",
  },
  editorWeb: {
    backToScript: "返回脚本",
    heroTitle: "选择配音 · 音乐 · 模板",
    estCostBadge: "约 ${{cost}}",
    tabSubtitleEngine: "字幕 + 引擎",
    sectionTemplateTitle: "9:16 竖屏,适配各短视频平台",
    sectionVoiceTitle: "{{n}} 个越南语 Neural 配音",
    sectionVoiceWarn:
      "⚠ 更改配音 = 全部场景重新 TTS(新 POST,不是 PATCH)。",
    sectionMusicTitle: "{{n}} 条已授权曲目",
    sectionMusicNoneTitle: "(无背景音乐)",
    sectionMusicNoneSub: "仅配音",
    sectionSubtitleTitle: "字幕样式",
    sectionEngineTitle: "渲染引擎",
    subtitleDefaultWhite: "基础白",
    subtitleBoldYellow: "亮黄",
    engineFastTitle: "Fast Mode",
    engineFastBody:
      "免费 · 约 3 秒渲染 · Ken Burns + TTS · 不占 AI 额度",
    engineAiTitle: "AI Mode",
    engineAiBody: "PRO · AI 主镜头 · Phase 2(即将推出)",
    templateSubtitle: "{{aspect}} · transition: {{transition}}",
    voiceSubtitleFemale: "♀ · {{locale}}",
    voiceSubtitleMale: "♂ · {{locale}}",
    musicSubtitle: "{{mood}} · {{bpm}} BPM · {{seconds}}s",
    musicLicense: "License: {{license}}",
    planTitle: "当前分镜",
    planBody:
      "{{scenes}} 个镜头 · 总 {{seconds}} 秒 · {{cacheHits}} 个缓存命中(免费)",
    saveBusy: "保存分镜…",
    saveUpdate: "保存更改 + 渲染 →",
    saveCreate: "创建分镜 + 渲染 →",
    missingTemplateOrVoice: "请先选择模板 + 配音。",
    needVariantFirst: "请先选择一个脚本方案再设置短视频。",
    saveFailPrefix: "无法保存。",
    sectionLoading: "加载选项…",
  },
  renderWeb: {
    backToEditor: "返回 Editor Lite",
    heroTitle: "预览还是最终?",
    heroBody:
      "预览免费 + 速度快,但带水印且画质较低。最终版占额度 + 较慢,但是干净的 1080×1920(付费版)。",
    fastPreviewTitle: "快速预览",
    fastPreviewBody: "约 60-90 秒 · iSale 水印 · 720p · 不占额度",
    fastPreviewBadge: "FREE",
    fastPreviewCta: "渲染预览",
    finalRenderTitle: "最终渲染",
    finalRenderBody: "约 3 分钟 · 1080×1920 · 按套餐水印 · 占 1 个视频",
    finalRenderBadge: "QUOTA",
    finalRenderCta: "渲染最终",
    queueingBusy: "准备中…",
    queuePreviewFail: "无法启动预览短片",
    queueFinalFail: "无法启动最终短片",
    cancelFail: "无法取消",
    initJob: "准备中…",
    pollingHint:
      "iSale 正在处理 — 可以关闭应用,完成时会推送通知。",
    stepLabel: "步骤:{{step}}",
    stepFallback: "初始化",
    doneTitle: "渲染完成",
    doneBody: "点击「下一步」导出 MP4 + 缩略图。",
    doneBodyEmbedded: "下载文件并发布 — 就在下方 👇",
    errorTitle: "渲染出错",
    errorUnknown: "未知错误。",
    cancelledTitle: "已取消",
    detailsTitle: "技术详情",
    detailsToggleShow: "查看技术详情",
    detailsToggleHide: "隐藏详情",
    rowId: "渲染编号",
    rowType: "类型",
    rowStatus: "状态",
    rowProgress: "进度",
    rowStep: "步骤",
    rowWatermark: "水印",
    rowCdnUrl: "CDN URL",
    watermarkYes: "是",
    watermarkNo: "否",
    fallbackDash: "—",
    cancelCta: "取消渲染",
    retryCta: "↻ 重试",
    openPreviewUrl: "打开预览 URL",
    copyUrl: "复制链接",
    nextExport: "下一步 → Export",
    // GA-W-09: preflight guard — no script yet
    preflightNoScriptTitle: "暂无脚本",
    preflightNoScriptBody:
      "渲染前需要先有脚本。请返回编辑器生成脚本。",
    preflightGoToEditorCta: "返回编辑器",
    // CF-H2: render reached before the clip is set up (no scene plan yet)
    preflightNoScenePlanTitle: "尚未设置短视频",
    preflightNoScenePlanBody:
      "渲染前请先在编辑器中选择模板、配音和音乐。",
    scenePlanRedirect: "尚未设置短视频 — 正在打开编辑器进行设置。",
    // CF-H4: client render timeout (taking longer than the worker ETA)
    stuckTitle: "比预期更久",
    stuckBody: "渲染时间异常偏长。请尝试刷新，或取消后重新渲染。",
    stuckRefreshCta: "刷新",
  },
  exportWeb: {
    backToRender: "返回 Render",
    pillLabel: "Export + Share",
    heroTitle: "下载 + 分享",
    heroBody:
      "可发 TikTok、Reels、Shorts、Shopee Video。Web 复制 URL + caption;移动端有原生分享面板。",
    watermarkTitle: "iSale 水印已开启",
    watermarkBody: "免费版渲染都带水印。升级 Pro 去除。",
    captionTitle: "Caption + 标签",
    platformLabel: "目标平台",
    platformHint: "话题标签按平台自动调整 — 不重新生成 AI",
    copyForPrefix: "复制到",
    captionLoading: "生成文案 + 标签…",
    captionFallback: "—",
    formatsTitle: "文件格式",
    attributionToggleTitle: '附加 "Made with iSale" ✨',
    attributionToggleNote:
      "复制 caption 时附加一行致谢。帮助其他人通过您的短片发现 iSale。",
    attributionLine: "由 iSale 制作 ✨ isale.app",
    celebrationTitle: "🎉 首支短片完成!",
    celebrationSubtitle: "不到 3 分钟 — 这就是 iSale 的承诺。",
    celebrationBody:
      "趁短片还新鲜立刻发布 — 算法偏爱新上传。复制下面的 caption + URL,直接发到 TikTok、Reels、Shorts、Shopee Video — caption 和 hashtag 我们已经准备好了。",
    celebrationCopyCta: "复制 caption + URL",
    celebrationUpgradeCta: "去水印 — 升级 Pro",
    celebrationDismissCta: "稍后",
    noRenderHint: "此短视频尚无成功的渲染。",
    goToRender: "前往 Render →",
    formatMp4: "MP4",
    formatThumbnail: "Thumbnail",
    formatSrt: "SRT",
    formatAll: "全部",
    existingTitle: "已导出({{n}})",
    existingEmpty: "点击上方按钮导出。",
    rowSizeExpires: "{{kb}} KB · 过期 {{date}}",
    copyUrlAria: "复制链接",
    openFileAria: "打开文件",
    openFileTitle: "打开 / 下载",
    copySharePrefix: "复制 caption + URL 用于发布",
    shareHint: "链接将在 24 小时后失效",
    copySuccess: "已复制 caption + 标签 + URL",
    copyNeedMp4: "暂无 MP4 — 请先点「全部」或「MP4」。",
    exportFailPrefix: "无法导出。",
    /** FL-5: mobile export hardcode sweep. */
    exportNeedRenderFirst: "导出前需要成功渲染。请先返回「渲染」页面。",
    exportSuccessCount: "已导出 {{n}} 个文件",
  },
  settingsWeb: {
    deleteContactSubtitle:
      "邮件至 privacy@isale.app · 团队将依照 GDPR 在 30 天内处理。",
    deleteUnderstood: "我知道了",
    deleteContactToast: "请联系 privacy@isale.app 删除数据",
    deleteRealSubtitle: "您的账户将被立即停用。在永久删除前您有 30 天的恢复时间。",
    deleteRealBody:
      "确认后:所有登录会话将退出。您的产品和短片将保留 30 天以便恢复。30 天后,您的所有产品、短片、照片和历史记录将依照 GDPR 永久删除。无需联系支持。",
    deleteConfirmCta: "删除我的账户",
    deleteBusy: "正在删除…",
    deleteSuccessToast: "账户已标记删除。您有 30 天的恢复时间。",
    deleteFailPrefix: "无法删除账户",
  },
  themeWeb: {
    back: "返回设置",
    intro: "选择外观模式。「自动」会跟随操作系统设置。",
    systemDesc: "通过 prefers-color-scheme 跟随系统主题",
    betaKicker: "BETA",
    betaBody:
      "部分旧页面暂未跟随主题切换。我们正在逐步完善,让整个界面保持一致。",
  },
  languageWeb: {
    back: "返回设置",
    intro:
      "越南语为默认语言。EN + ZH 词条已预译完毕 — i18n 迁移完成后将全面覆盖各屏幕。",
    requiresReloadSuffix: "· 需要刷新标签页",
    reloadTitle: "需要刷新标签页",
    reloadToZh: "切换到中文需刷新以加载最佳的 CJK 字体。",
    reloadFromZh: "从中文切换需刷新以释放 CJK 字体。",
    reloadCta: "切换并刷新",
    skipReloadCta: "切换但不刷新",
    reloadToast: "已切换语言 · 请刷新标签页以完成。",
    localOnlyWarning: "本地切换成功;尚未与服务器同步({{error}})",
  },
  oauthCallback: {
    working: "正在完成登录…",
    redirecting: "正在跳转…",
    errorMissing: "Google 响应中缺少必要数据",
    errorWithCode: "Google 拒绝 · {{code}}",
    errorExchange: "无法兑换会话 · {{error}}",
  },
  profileEditWeb: {
    back: "返回设置",
    saveFail: "保存失败 · {{error}}",
    loadErrorFallback: "请检查后端连接。",
    avatarUploading: "上传中…",
    avatarUploaded: "头像已更新",
    avatarUploadFail: "头像上传失败 · {{error}}",
    avatarTooLarge: "图片过大 — 最大 5MB",
    breadcrumbRoot: "设置",
    breadcrumbCurrent: "个人资料",
    h1: "个人资料",
    joinedAt: "加入于 {{date}}",
    phoneLabel: "电话",
    phonePlaceholder: "+84 ...",
    tiktokLabel: "店铺 / 社交链接",
    tiktokPlaceholder: "@your-handle",
    bioLabel: "店铺简介",
    bioPlaceholder: "用一句话介绍你的店铺(最多 200 个字符)。",
    avatarChipLabel: "更换头像",
  },
  settingsSubnav: {
    kicker: "设置",
    profile: "个人资料",
    appearance: "外观",
    language: "语言",
    notifications: "通知",
    privacy: "权限与数据",
    quota: "配额与套餐",
    aiActivity: "AI 活动",
  },
  planAside: {
    kicker: "当前套餐",
    planFree: "免费版",
    planPro: "Pro",
    usageLine: "{{used}}/{{cap}} 视频 · 剩 {{remaining}}",
    upgradeCta: "升级 Pro",
    invoicesTitle: "最近 3 张账单",
    invoiceLine: "{{date}} · ${{amount}} {{status}}",
    invoiceOk: "✓",
    invoiceFail: "✗",
  },
  notifPrefs: {
    breadcrumbCurrent: "通知",
    h1: "通知",
    groupRender: "渲染与导出",
    groupQuota: "配额与套餐",
    groupEmail: "邮件",
    renderDone: "短片渲染完成",
    renderFailed: "渲染失败",
    renderReady: "短片可下载",
    quotaWarn: "剩 20% 配额",
    quotaFull: "本月配额用尽",
    quotaPromo: "升级优惠",
    emailWeekly: "每周摘要",
    previewKicker: "预览",
    previewPushHint: "📱 手机推送",
    previewToastHint: "💬 应用内提示",
    previewAppName: "iSale",
    previewPushTitle: "✓ 短片 \"Serum HA\" 已就绪",
    previewPushBody: "点击查看 · 30 秒 · 9:16",
    previewToastBody: "渲染完成 · 30 秒",
    previewFootnote: "预览随你切换选项而更新。",
    previewDisabled: "已关闭",
    saveBusy: "保存中…",
    savedLocal: "通知设置已保存(本地)",
  },
  nav: {
    dashboard: "首页",
    products: "商品",
    clips: "短片",
    billing: "套餐",
    aiActivity: "AI 活动",
    settings: "设置",
    workspace: "工作区",
    signOut: "退出登录",
    signOutSuccess: "已退出登录",
  },
  routes: {
    dashboard: "首页",
    products: "商品",
    clips: "短片",
    clipNew: "制作短视频",
    projectScript: "脚本",
    projectDetailParent: "短片详情",
    editor: "Editor Lite",
    render: "渲染",
    export: "导出 + 分享",
    billing: "套餐",
    aiActivity: "AI 活动",
    settings: "设置",
    settingsTheme: "外观",
    settingsLanguage: "语言",
    settingsNotifications: "通知",
    settingsOnboarding: "重新查看指南",
    profileEdit: "编辑资料",
    productDetail: "商品详情",
    productFacts: "确认事实",
    clipTemplates: "短视频模板",
    clipTemplateDetail: "模板详情",
  },
  // MP10 — Transactional email templates (5 templates × 3 locales)
  emails: {
    shared: {
      footer: "iSale 自动邮件 · 无需回复",
      helpLink: "帮助",
      privacyLink: "隐私",
      tosLink: "条款",
    },
    welcome: {
      subject: "欢迎来到 iSale 🎉",
      preheader: "3 分钟创建第一个销售短视频",
      heading: "你好 {{name}} 👋",
      body1:
        "用 AI 把产品照片变成销售短片 — 无需会剪辑。拍 3-5 张照片,iSale 自动生成脚本、越南语配音、9:16 短片,可发布到 TikTok、Reels、Shorts、Shopee Video。",
      cta: "开始创建",
      freeTierNote: "您正在使用 Free 套餐 — 每月 5 个免费短片。",
    },
    renderDone: {
      subject: "{{name}} 短片已就绪 🎬",
      preheader: "打开预览 — 您的渲染刚完成",
      heading: "{{name}} 短片完成",
      body1:
        "发布前预览 — 可直接下载 MP4 或通过下方链接分享到 TikTok、Reels、Shorts、Shopee Video。",
      meta: "{{duration}} · {{size}}",
      cta: "查看短片",
      ctaSecondary: "或在应用中打开:",
    },
    renderFailed: {
      subject: "{{name}} 渲染未完成",
      preheader: "不消耗配额 — 可免费重试",
      heading: "渲染过程中出了点小问题",
      body1:
        "不是您的问题 — 我们将免费重试,不计入配额。点击下方按钮立即重新渲染。",
      errorCodeLabel: "错误代码: {{code}}",
      fallbackSuggestion: "建议: 尝试 {{provider}} 提供商(稍慢但更稳定)。",
      noQuotaCharge: "本次失败的渲染不计入配额。",
      cta: "免费重试",
    },
    quotaWarn: {
      subjectWarn: "本月渲染配额即将用尽",
      subjectFull: "本月渲染配额已用尽",
      preheader: "您已使用 {{used}}/{{cap}} 个短片",
      headingWarn: "本月配额即将用尽",
      headingFull: "本月配额已用尽",
      body1:
        "您已使用 {{used}}/{{cap}} 个短片。配额将在 {{days}} 天内重置,或升级到 Pro 立即继续。",
      cta: "升级到 Pro",
      tip: "Pro: 每月 150 个视频 + 去除水印 + AI 主场景。",
    },
    paymentFailed: {
      subject: "付款未能处理",
      preheader: "更新卡片以保留 Pro 权益",
      heading: "付款失败",
      body1:
        "Stripe 无法从现有卡片扣款。您的账户在本计费周期结束前仍为 Pro — 只需在下次重试前更新卡片,即可避免降级到 Free。",
      attemptCount: "第 {{n}} 次尝试",
      nextRetryAt: "Stripe 将于 {{date}} 重试",
      cta: "更新支付方式",
      supportLink: "遇到问题?发送邮件至 support@isale.app",
    },
  },
} as const;

export default zh;
